import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Match, MatchEvent, SerializableMatch, deleteMatchEvent, getMatch, convertToSerializableMatch } from '../../services/matchService';
import { RootState } from '../../store/store';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { deleteEvent, updateScore, setCurrentMatch } from '../../store/matchSlice';
import { useEffect, useMemo } from 'react';

interface AutoTableOptions {
  startY: number;
  head: string[][];
  body: string[][];
}

type JsPDFWithAutoTable = jsPDF & {
  autoTable: (options: AutoTableOptions) => void;
  lastAutoTable: {
    finalY: number;
  };
}


interface MatchStatsProps {
  match: Match | SerializableMatch;
}

interface TeamStats {
  goals: { count: number; times: string[] };
  assists: { count: number; times: string[] };
  yellowCards: { count: number; times: string[] };
  redCards: { count: number; times: string[] };
  substitutions: { count: number; times: string[] };
  injuries: { count: number; times: string[] };
  chances: { count: number; times: string[] };
  shotsOnTarget: { count: number; times: string[] };
  ownGoals: { count: number; times: string[] }; // csc
  corners: { count: number; times: string[] }; // corner
  freeKicksDirect: { count: number; times: string[] }; // cfd
  freeKicksIndirect: { count: number; times: string[] }; // cfi
  offsides: { count: number; times: string[] }; // hj
  recoveries: { count: number; times: string[] }; // recup
  losses: { count: number; times: string[] }; // perte
}

const MatchStats: React.FC<MatchStatsProps> = ({ match: originalMatch }) => {
  // Créer une copie profonde du match pour éviter les erreurs de modification d'objets en lecture seule
  const match = JSON.parse(JSON.stringify(originalMatch)) as Match | SerializableMatch;
  
  // Obtenir le match courant depuis Redux
  const currentMatch = useSelector((state: RootState) => state.match.currentMatch);
  const dispatch = useDispatch();
  
  // État local pour les événements (pour mise à jour immédiate de l'UI)
  const [localEvents, setLocalEvents] = React.useState<(MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string })[]>([]);
  
  // Utiliser les scores depuis Redux si disponibles
  const matchFromRedux = currentMatch && currentMatch.id === match.id ? 
    JSON.parse(JSON.stringify(currentMatch)) : match;
  const scoreA = matchFromRedux.scoreA;
  const scoreB = matchFromRedux.scoreB;
  
  // Utiliser les événements locaux pour les statistiques
  const eventsToUse = useMemo(() => {
    return localEvents.length > 0 ? localEvents : (matchFromRedux.events || []);
  }, [localEvents, matchFromRedux.events]);

  // Log détaillé des scores et initialisation des événements locaux
  // Initialiser les événements locaux à partir du match (une seule fois au montage ou lors d'un changement de match)
  useEffect(() => {
    console.log('[MATCH STATS] Score information', {
      matchId: match.id,
      matchScores: {
        scoreA: match.scoreA,
        scoreB: match.scoreB
      },
      reduxCurrentMatch: currentMatch ? {
        id: currentMatch.id,
        scoreA: currentMatch.scoreA,
        scoreB: currentMatch.scoreB,
        events: currentMatch.events?.length
      } : null,
      calculatedScores: {
        scoreA,
        scoreB
      },
      events: matchFromRedux.events?.length
    });
    
    // Initialiser les événements locaux à partir du match seulement si localEvents est vide
    if (matchFromRedux.events && matchFromRedux.events.length > 0 && localEvents.length === 0) {
      setLocalEvents(matchFromRedux.events);
    }
  }, [match.id, currentMatch?.id]); // Dépendances réduites pour éviter les boucles infinies

  // Définir les types d'événements avec les mêmes icônes/labels que LiveMatchControls
  const eventTypes = [
    { type: 'goal', label: 'But', emoji: '⚽' },
    { type: 'passe_decisive', label: 'Passe D.', emoji: '👥' },
    { type: 'tir_cadre', label: 'T Cadré', emoji: '⚽' },
    { type: 'occasion', label: 'Occas.', emoji: '⚡' },
    { type: 'csc', label: 'CSC', emoji: '❌' },
    { type: 'corner', label: 'Corner', emoji: '↗️' },
    { type: 'cfd', label: 'CF Direct', emoji: '🎯' },
    { type: 'cfi', label: 'CF Indirect', emoji: '🎯' },
    { type: 'recup', label: 'Récup.', emoji: '🔄' },
    { type: 'perte', label: 'Perte', emoji: '❌' },
    { type: 'hj', label: 'Hors Jeu', emoji: '🚩' },
    { type: 'remplacement', label: 'Change', emoji: '🔄' },
    { type: 'blesse', label: 'Blessé', emoji: '🩺' },
    { type: 'jaune', label: 'Jaune', emoji: '🟨' },
    { type: 'rouge', label: 'Rouge', emoji: '🟥' }
  ];

  // Ordre d'affichage 3 lignes x 5 colonnes (gauche -> milieu -> droite comme en édition)
  const orderedEventGrid = [
    'goal', 'passe_decisive', 'tir_cadre', 'occasion', 'csc',
    'corner', 'cfd', 'cfi', 'recup', 'perte',
    'hj', 'remplacement', 'blesse', 'jaune', 'rouge'
  ] as const;

  // Fonction pour exporter les données au format Excel
  const handleExportExcel = () => {
    try {
      // Récupérer les événements selon la période sélectionnée
      const eventsToExport = eventsToUse;
      
      // Trier les événements par minute
      const sortedEvents = [...eventsToExport].sort((a, b) => {
        return (a.minute * 60 + (a.seconds || 0)) - (b.minute * 60 + (b.seconds || 0));
      });
      
      // Préparer les données pour l'export Excel
      const eventsData = sortedEvents.map(event => {
        const eventType = eventTypes.find(type => type.type === event.type);
        const teamName = event.team === 'teamA' ? match.teamA.name : match.teamB.name;
        return {
          'Temps': `${event.minute}'${event.seconds ? `:${event.seconds}` : ''}`,
          'Équipe': teamName,
          'Action': eventType?.label || event.type,
          'Joueur': event.player || ''
        };
      });
      
      // Préparer les statistiques globales
      const teamAEvents = eventsToExport.filter((event: any) => event.team === 'teamA');
      const teamBEvents = eventsToExport.filter((event: any) => event.team === 'teamB');
      
      const filterEvents = (events: any[], type: string) => events.filter((e: any) => e.type === type).length;
      
      const statsData = [
        { 'Statistique': 'Buts', [match.teamA.name]: filterEvents(teamAEvents, 'goal'), [match.teamB.name]: filterEvents(teamBEvents, 'goal') },
        { 'Statistique': 'Passe D.', [match.teamA.name]: filterEvents(teamAEvents, 'passe_decisive'), [match.teamB.name]: filterEvents(teamBEvents, 'passe_decisive') },
        { 'Statistique': 'T Cadré', [match.teamA.name]: filterEvents(teamAEvents, 'tir_cadre'), [match.teamB.name]: filterEvents(teamBEvents, 'tir_cadre') },
        { 'Statistique': 'Occas.', [match.teamA.name]: filterEvents(teamAEvents, 'occasion'), [match.teamB.name]: filterEvents(teamBEvents, 'occasion') },
        { 'Statistique': 'Blessé', [match.teamA.name]: filterEvents(teamAEvents, 'blesse'), [match.teamB.name]: filterEvents(teamBEvents, 'blesse') },
        { 'Statistique': 'Jaune', [match.teamA.name]: filterEvents(teamAEvents, 'jaune'), [match.teamB.name]: filterEvents(teamBEvents, 'jaune') },
        { 'Statistique': 'Rouge', [match.teamA.name]: filterEvents(teamAEvents, 'rouge'), [match.teamB.name]: filterEvents(teamBEvents, 'rouge') },
        { 'Statistique': 'Change', [match.teamA.name]: filterEvents(teamAEvents, 'remplacement'), [match.teamB.name]: filterEvents(teamBEvents, 'remplacement') },
        { 'Statistique': 'CSC', [match.teamA.name]: filterEvents(teamAEvents, 'csc'), [match.teamB.name]: filterEvents(teamBEvents, 'csc') },
        { 'Statistique': 'Corners', [match.teamA.name]: filterEvents(teamAEvents, 'corner'), [match.teamB.name]: filterEvents(teamBEvents, 'corner') },
        { 'Statistique': 'CFD', [match.teamA.name]: filterEvents(teamAEvents, 'cfd'), [match.teamB.name]: filterEvents(teamBEvents, 'cfd') },
        { 'Statistique': 'CFI', [match.teamA.name]: filterEvents(teamAEvents, 'cfi'), [match.teamB.name]: filterEvents(teamBEvents, 'cfi') },
        { 'Statistique': 'HJ', [match.teamA.name]: filterEvents(teamAEvents, 'hj'), [match.teamB.name]: filterEvents(teamBEvents, 'hj') },
        { 'Statistique': 'Récup.', [match.teamA.name]: filterEvents(teamAEvents, 'recup'), [match.teamB.name]: filterEvents(teamBEvents, 'recup') },
        { 'Statistique': 'Pertes', [match.teamA.name]: filterEvents(teamAEvents, 'perte'), [match.teamB.name]: filterEvents(teamBEvents, 'perte') }
      ];
      
      // Créer un classeur Excel avec plusieurs feuilles
      const wb = XLSX.utils.book_new();
      
      // Ajouter la feuille des événements
      const wsEvents = XLSX.utils.json_to_sheet(eventsData);
      XLSX.utils.book_append_sheet(wb, wsEvents, 'Chronologie');
      
      // Ajouter la feuille des statistiques
      const wsStats = XLSX.utils.json_to_sheet(statsData);
      XLSX.utils.book_append_sheet(wb, wsStats, 'Statistiques');
      
      // Ajouter une feuille d'information sur le match
      // Gestion du type de date qui peut être Timestamp, string ou Date
      const matchDate = (() => {
        if (!match.date) return null;
        
        // Si c'est déjà un objet Date
        if (match.date instanceof Date) {
          return match.date;
        }
        
        // Si c'est un Timestamp Firestore (avec une propriété seconds)
        if (typeof match.date === 'object' && 'seconds' in match.date) {
          return new Date(match.date.seconds * 1000);
        }
        
        // Si c'est une chaîne de caractères (date ISO)
        if (typeof match.date === 'string') {
          return new Date(match.date);
        }
        
        // Pour tout autre cas, retourner la date actuelle
        return new Date();
      })();

      const matchInfo = [
        { 'Information': 'Date', 'Valeur': matchDate ? matchDate.toLocaleDateString() : 'N/A' },
        { 'Information': 'Équipe A', 'Valeur': match.teamA.name },
        { 'Information': 'Équipe B', 'Valeur': match.teamB.name },
        { 'Information': 'Score', 'Valeur': `${match.scoreA} - ${match.scoreB}` },
        { 'Information': 'Lieu', 'Valeur': 'N/A' } // La propriété location n'existe pas dans l'interface Match
      ];
      const wsInfo = XLSX.utils.json_to_sheet(matchInfo);
      XLSX.utils.book_append_sheet(wb, wsInfo, 'Informations');
      
      // Générer le fichier Excel
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Télécharger le fichier
      saveAs(data, `Match_${match.teamA.name}_vs_${match.teamB.name}.xlsx`);
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      alert('Une erreur est survenue lors de l\'export Excel. Veuillez réessayer.');
    }
  };

  // Note: eventsToUse est maintenant défini plus haut avec useMemo

  // Fonction pour formater le temps d'un événement
  const formatEventTime = (event: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }): string => {
    return event.seconds ? `${event.minute}'${event.seconds}"` : `${event.minute}'`;
  };

  // Fonction pour calculer les stats d'une équipe
  const calculateTeamStats = (events: (MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string })[], team: string): TeamStats => {
    // Utiliser les événements locaux pour les calculs
    const teamEvents = events.filter(e => e.team === team);
  
    const getEventStats = (type: string) => {
      const filteredEvents = teamEvents.filter(e => e.type === type);
      return {
        count: filteredEvents.length,
        times: filteredEvents.map(e => formatEventTime(e as MatchEvent))
      };
    };

    return {
      goals: getEventStats('goal'),
      assists: getEventStats('passe_decisive'),
      yellowCards: getEventStats('jaune'),
      redCards: getEventStats('rouge'),
      substitutions: getEventStats('remplacement'),
      injuries: getEventStats('blesse'),
      chances: getEventStats('occasion'),
      shotsOnTarget: getEventStats('tir_cadre'),
      ownGoals: getEventStats('csc'),
      corners: getEventStats('corner'),
      freeKicksDirect: getEventStats('cfd'),
      freeKicksIndirect: getEventStats('cfi'),
      offsides: getEventStats('hj'),
      recoveries: getEventStats('recup'),
      losses: getEventStats('perte')
    };
  };

  // Fonction pour calculer les stats d'une période
  const calculatePeriodStats = (periodName: string, events: (MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string })[]) => {
    // Créer une copie du tableau pour éviter les erreurs de modification d'objets en lecture seule
    const eventsCopy = [...events];
  
    return {
      periodName,
      stats: {
        goals: eventsCopy.filter(event => event.type === 'goal').length,
        assists: eventsCopy.filter(event => event.type === 'passe_decisive').length,
        yellowCards: eventsCopy.filter(event => event.type === 'jaune').length,
        redCards: eventsCopy.filter(event => event.type === 'rouge').length,
        substitutions: eventsCopy.filter(event => event.type === 'remplacement').length,
        injuries: eventsCopy.filter(event => event.type === 'blesse').length,
        chances: eventsCopy.filter(event => event.type === 'occasion').length,
        shotsOnTarget: eventsCopy.filter(event => event.type === 'tir_cadre').length,
        ownGoals: eventsCopy.filter(event => event.type === 'csc').length,
        corners: eventsCopy.filter(event => event.type === 'corner').length,
        freeKicksDirect: eventsCopy.filter(event => event.type === 'cfd').length,
        freeKicksIndirect: eventsCopy.filter(event => event.type === 'cfi').length,
        offsides: eventsCopy.filter(event => event.type === 'hj').length,
        recoveries: eventsCopy.filter(event => event.type === 'recup').length,
        losses: eventsCopy.filter(event => event.type === 'perte').length
      },
      events: eventsCopy
    };
  };

  // Utiliser useMemo pour calculer les statistiques sans mutation d'objets
  const teamAStats = useMemo(() => calculateTeamStats(eventsToUse, 'teamA'), [eventsToUse]);
  const teamBStats = useMemo(() => calculateTeamStats(eventsToUse, 'teamB'), [eventsToUse]);

  // Valeurs par défaut pour les statistiques totales
  const emptyTotalStats = {
    goals: 0,
    assists: 0,
    yellowCards: 0,
    redCards: 0,
    substitutions: 0,
    injuries: 0,
    chances: 0,
    shotsOnTarget: 0,
    ownGoals: 0,
    corners: 0,
    freeKicksDirect: 0,
    freeKicksIndirect: 0,
    offsides: 0,
    recoveries: 0,
    losses: 0
  };

  // Calculer les statistiques par période
  const { periodStats: calculatedPeriodStats, totalStats } = useMemo(() => {
    const periodsMap: { [key: string]: (MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string })[] } = {};
    
    // Regrouper les événements par période
    eventsToUse.forEach((event: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }) => {
      const period = event.period || '1';
      if (!periodsMap[period]) {
        periodsMap[period] = [];
      }
      periodsMap[period].push(event);
    });
    
    // Calculer les stats pour chaque période
    const periodNames: { [key: string]: string } = {
      '1': 'Mi-temps 1',
      '2': 'Mi-temps 2',
      '3': 'Prolongation 1',
      '4': 'Prolongation 2',
      '5': 'Tirs au but'
    };
    
    const periodsList = Object.keys(periodsMap).map(periodKey => {
      const periodEvents = periodsMap[periodKey];
      const teamAEvents = periodEvents.filter(e => e.team === 'teamA');
      const teamBEvents = periodEvents.filter(e => e.team === 'teamB');
      
      const teamAStats = calculateTeamStats(teamAEvents, 'teamA');
      const teamBStats = calculateTeamStats(teamBEvents, 'teamB');
      
      // Fusionner les stats des deux équipes pour l'ancienne structure
      const mergedStats = {
        goals: teamAStats.goals.count + teamBStats.goals.count,
        assists: teamAStats.assists.count + teamBStats.assists.count,
        yellowCards: teamAStats.yellowCards.count + teamBStats.yellowCards.count,
        redCards: teamAStats.redCards.count + teamBStats.redCards.count,
        substitutions: teamAStats.substitutions.count + teamBStats.substitutions.count,
        injuries: teamAStats.injuries.count + teamBStats.injuries.count,
        chances: teamAStats.chances.count + teamBStats.chances.count,
        shotsOnTarget: teamAStats.shotsOnTarget.count + teamBStats.shotsOnTarget.count,
        ownGoals: teamAStats.ownGoals.count + teamBStats.ownGoals.count,
        corners: teamAStats.corners.count + teamBStats.corners.count,
        freeKicksDirect: teamAStats.freeKicksDirect.count + teamBStats.freeKicksDirect.count,
        freeKicksIndirect: teamAStats.freeKicksIndirect.count + teamBStats.freeKicksIndirect.count,
        offsides: teamAStats.offsides.count + teamBStats.offsides.count,
        recoveries: teamAStats.recoveries.count + teamBStats.recoveries.count,
        losses: teamAStats.losses.count + teamBStats.losses.count
      };
      
      return {
        periodName: periodNames[periodKey] || `Période ${periodKey}`,
        periodKey,
        stats: mergedStats,
        events: periodEvents
      };
    });
    
    // Calculer les stats totales (ancienne structure pour compatibilité)
    const teamATotalEvents = eventsToUse.filter((e: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }) => e.team === 'teamA');
    const teamBTotalEvents = eventsToUse.filter((e: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }) => e.team === 'teamB');
    
    const teamATotalStats = calculateTeamStats(teamATotalEvents, 'teamA');
    const teamBTotalStats = calculateTeamStats(teamBTotalEvents, 'teamB');
    
    // Fusionner les stats des deux équipes pour l'ancienne structure
    const mergedTotalStats = {
      goals: teamATotalStats.goals.count + teamBTotalStats.goals.count,
      assists: teamATotalStats.assists.count + teamBTotalStats.assists.count,
      yellowCards: teamATotalStats.yellowCards.count + teamBTotalStats.yellowCards.count,
      redCards: teamATotalStats.redCards.count + teamBTotalStats.redCards.count,
      substitutions: teamATotalStats.substitutions.count + teamBTotalStats.substitutions.count,
      injuries: teamATotalStats.injuries.count + teamBTotalStats.injuries.count,
      chances: teamATotalStats.chances.count + teamBTotalStats.chances.count,
      shotsOnTarget: teamATotalStats.shotsOnTarget.count + teamBTotalStats.shotsOnTarget.count,
      ownGoals: teamATotalStats.ownGoals.count + teamBTotalStats.ownGoals.count,
      corners: teamATotalStats.corners.count + teamBTotalStats.corners.count,
      freeKicksDirect: teamATotalStats.freeKicksDirect.count + teamBTotalStats.freeKicksDirect.count,
      freeKicksIndirect: teamATotalStats.freeKicksIndirect.count + teamBTotalStats.freeKicksIndirect.count,
      offsides: teamATotalStats.offsides.count + teamBTotalStats.offsides.count,
      recoveries: teamATotalStats.recoveries.count + teamBTotalStats.recoveries.count,
      losses: teamATotalStats.losses.count + teamBTotalStats.losses.count
    };
    
    return {
      periodStats: periodsList,
      totalStats: mergedTotalStats
    };
  }, [eventsToUse, calculateTeamStats]);

  // Fonction pour formater l'affichage des statistiques
  const formatStatDisplay = (stat: { count: number; times: string[] }) => {
    if (stat.count === 0) return "0";
    return `${stat.count} (${stat.times.join(', ')})`;
  };

  // Si aucun événement, afficher un message
  if (eventsToUse.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        Aucun événement disponible pour ce match
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6">
      {/* En-tête du match - Version mobile */}
      <div className="md:hidden">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">{match.teamA.name}</h2>
          <div className="text-3xl font-bold">{scoreA} - {scoreB}</div>
          <h2 className="text-2xl font-bold">{match.teamB.name}</h2>
          <div className="text-sm text-gray-500">
            {typeof match.date === 'string' ? new Date(match.date).toLocaleDateString('fr-FR') : new Date((match.date as any).toMillis ? (match.date as any).toMillis() : match.date).toLocaleDateString('fr-FR')}
          </div>
        </div>
      </div>

      {/* En-tête du match - Version desktop */}
      <div className="hidden md:block">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">{match.teamA.name}</h2>
          <div className="text-center">
            <div className="text-3xl font-bold mb-2">{scoreA} - {scoreB}</div>
            <div className="text-sm text-gray-500">
              {typeof match.date === 'string' ? new Date(match.date).toLocaleDateString('fr-FR') : new Date((match.date as any).toMillis ? (match.date as any).toMillis() : match.date).toLocaleDateString('fr-FR')}
            </div>
          </div>
          <h2 className="text-2xl font-bold">{match.teamB.name}</h2>
        </div>
      </div>

      {/* Statistiques globales */}
      <div className="mt-3 md:mt-6 space-y-2 md:space-y-4">
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Statistiques Globales
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {orderedEventGrid.map((typeKey) => {
            const info = eventTypes.find(e => e.type === typeKey)!;
            const value = (
              typeKey === 'goal' ? totalStats.goals :
              typeKey === 'passe_decisive' ? totalStats.assists :
              typeKey === 'tir_cadre' ? totalStats.shotsOnTarget :
              typeKey === 'occasion' ? totalStats.chances :
              typeKey === 'csc' ? totalStats.ownGoals :
              typeKey === 'corner' ? totalStats.corners :
              typeKey === 'cfd' ? totalStats.freeKicksDirect :
              typeKey === 'cfi' ? totalStats.freeKicksIndirect :
              typeKey === 'recup' ? totalStats.recoveries :
              typeKey === 'perte' ? totalStats.losses :
              typeKey === 'hj' ? totalStats.offsides :
              typeKey === 'remplacement' ? totalStats.substitutions :
              typeKey === 'blesse' ? totalStats.injuries :
              typeKey === 'jaune' ? totalStats.yellowCards :
              /* rouge */ totalStats.redCards
            );
            return (
              <div key={typeKey} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-4 hover:shadow-md transition-all">
                <div className="text-3xl">{info.emoji}</div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">{info.label}</h4>
                  <p className="text-2xl font-bold text-gray-700">{value}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Statistiques par équipe - Version mobile */}
      <div className="md:hidden space-y-6">
        {/* Équipe A */}
        <div className="bg-gray-100 p-1 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 text-center mx-4">{match.teamA.name}</h3>
          <div className="space-y-4">
            {teamAStats.goals.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Buts ({teamAStats.goals.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.goals.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.assists.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Passes décisives ({teamAStats.assists.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.assists.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.yellowCards.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Jaune ({teamAStats.yellowCards.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.yellowCards.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.redCards.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Rouge ({teamAStats.redCards.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.redCards.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.substitutions.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Remplacements ({teamAStats.substitutions.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.substitutions.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.injuries.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Blessés ({teamAStats.injuries.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.injuries.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.chances.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Occasions ({teamAStats.chances.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.chances.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.shotsOnTarget.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Tirs cadrés ({teamAStats.shotsOnTarget.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.shotsOnTarget.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.ownGoals.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CSC ({teamAStats.ownGoals.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.ownGoals.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.corners.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Corners ({teamAStats.corners.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.corners.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.freeKicksDirect.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFD ({teamAStats.freeKicksDirect.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.freeKicksDirect.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.freeKicksIndirect.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFI ({teamAStats.freeKicksIndirect.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.freeKicksIndirect.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.offsides.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Hors-jeu ({teamAStats.offsides.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.offsides.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.recoveries.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Récupérations ({teamAStats.recoveries.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.recoveries.times.join(', ')}</div>
              </div>
            )}
            {teamAStats.losses.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Pertes ({teamAStats.losses.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.losses.times.join(', ')}</div>
              </div>
            )}
          </div>
        </div>

        {/* Équipe B */}
        <div className="bg-gray-100 p-1 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 text-center mx-4">{match.teamB.name}</h3>
          <div className="space-y-4">
            {teamBStats.goals.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Buts ({teamBStats.goals.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.goals.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.assists.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Passes décisives ({teamBStats.assists.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.assists.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.yellowCards.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Jaune ({teamBStats.yellowCards.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.yellowCards.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.redCards.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Rouge ({teamBStats.redCards.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.redCards.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.substitutions.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Remplacements ({teamBStats.substitutions.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.substitutions.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.injuries.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Blessés ({teamBStats.injuries.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.injuries.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.chances.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Occasions ({teamBStats.chances.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.chances.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.shotsOnTarget.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Tirs cadrés ({teamBStats.shotsOnTarget.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.shotsOnTarget.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.ownGoals.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CSC ({teamBStats.ownGoals.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.ownGoals.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.corners.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Corners ({teamBStats.corners.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.corners.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.freeKicksDirect.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFD ({teamBStats.freeKicksDirect.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.freeKicksDirect.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.freeKicksIndirect.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFI ({teamBStats.freeKicksIndirect.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.freeKicksIndirect.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.offsides.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Hors-jeu ({teamBStats.offsides.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.offsides.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.recoveries.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Récupérations ({teamBStats.recoveries.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.recoveries.times.join(', ')}</div>
              </div>
            )}
            {teamBStats.losses.count > 0 && (
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Pertes ({teamBStats.losses.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.losses.times.join(', ')}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistiques par équipe - Version desktop */}
      <div className="hidden md:grid md:grid-cols-2 gap-6">
        {/* Équipe A */}
        <div className="bg-gray-100 p-1 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 text-center mx-4">{match.teamA.name}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-2 md:gap-4 mb-3 md:mb-6">
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Buts ({teamAStats.goals.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.goals.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Passes décisives ({teamAStats.assists.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.assists.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Jaune ({teamAStats.yellowCards.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.yellowCards.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Rouge ({teamAStats.redCards.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.redCards.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Remplacements ({teamAStats.substitutions.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.substitutions.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Blessés ({teamAStats.injuries.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.injuries.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Occasions ({teamAStats.chances.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.chances.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Tirs cadrés ({teamAStats.shotsOnTarget.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.shotsOnTarget.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CSC ({teamAStats.ownGoals.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.ownGoals.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Corners ({teamAStats.corners.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.corners.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFD ({teamAStats.freeKicksDirect.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.freeKicksDirect.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFI ({teamAStats.freeKicksIndirect.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.freeKicksIndirect.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Hors-jeu ({teamAStats.offsides.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.offsides.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Récupérations ({teamAStats.recoveries.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.recoveries.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Pertes ({teamAStats.losses.count})</div>
                <div className="text-sm text-gray-600">{teamAStats.losses.times.join(', ')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Équipe B */}
        <div className="bg-gray-100 p-1 rounded-lg">
          <h3 className="text-xl font-semibold mb-4 text-center mx-4">{match.teamB.name}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-2 md:gap-4 mb-3 md:mb-6">
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Buts ({teamBStats.goals.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.goals.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Passes décisives ({teamBStats.assists.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.assists.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Jaune ({teamBStats.yellowCards.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.yellowCards.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Rouge ({teamBStats.redCards.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.redCards.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Remplacements ({teamBStats.substitutions.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.substitutions.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Blessés ({teamBStats.injuries.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.injuries.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Occasions ({teamBStats.chances.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.chances.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Tirs cadrés ({teamBStats.shotsOnTarget.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.shotsOnTarget.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CSC ({teamBStats.ownGoals.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.ownGoals.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Corners ({teamBStats.corners.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.corners.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFD ({teamBStats.freeKicksDirect.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.freeKicksDirect.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">CFI ({teamBStats.freeKicksIndirect.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.freeKicksIndirect.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Hors-jeu ({teamBStats.offsides.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.offsides.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Récupérations ({teamBStats.recoveries.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.recoveries.times.join(', ')}</div>
              </div>
              <div className="bg-gray-50 p-2 rounded-lg shadow-sm">
                <div className="font-semibold">Pertes ({teamBStats.losses.count})</div>
                <div className="text-sm text-gray-600">{teamBStats.losses.times.join(', ')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques par période */}
      <div className="bg-gray-100 p-1 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-center mx-4">Statistiques par période</h3>
        <div className="space-y-3 md:space-y-6">
          {calculatedPeriodStats.map((period, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <h4 className="text-lg font-semibold mb-2 text-center py-2 bg-gray-50">{period.periodName}</h4>

              {/* Grille 3x5 avec même ordre/icônes que l’édition */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4">
                {orderedEventGrid.map((typeKey) => {
                  const info = eventTypes.find(e => e.type === typeKey)!;
                  const value = (
                    typeKey === 'goal' ? period.stats.goals :
                    typeKey === 'passe_decisive' ? period.stats.assists :
                    typeKey === 'tir_cadre' ? period.stats.shotsOnTarget :
                    typeKey === 'occasion' ? period.stats.chances :
                    typeKey === 'csc' ? period.stats.ownGoals :
                    typeKey === 'corner' ? period.stats.corners :
                    typeKey === 'cfd' ? period.stats.freeKicksDirect :
                    typeKey === 'cfi' ? period.stats.freeKicksIndirect :
                    typeKey === 'recup' ? period.stats.recoveries :
                    typeKey === 'perte' ? period.stats.losses :
                    typeKey === 'hj' ? period.stats.offsides :
                    typeKey === 'remplacement' ? period.stats.substitutions :
                    typeKey === 'blesse' ? period.stats.injuries :
                    typeKey === 'jaune' ? period.stats.yellowCards :
                    /* rouge */ period.stats.redCards
                  );
                  return (
                    <div key={typeKey} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-4 hover:shadow-md transition-all">
                      <div className="text-2xl md:text-3xl">{info.emoji}</div>
                      <div>
                        <h5 className="text-xs md:text-sm font-semibold text-gray-800">{info.label}</h5>
                        <p className="text-xl md:text-2xl font-bold text-gray-700">{value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tableau chronologique des actions */}
      <div className="mt-3 md:mt-6 space-y-2 md:space-y-4">
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Chronologie du Match
        </h3>
        <div className="bg-gradient-to-br from-white to-gray-50 shadow-lg rounded-xl overflow-hidden border border-gray-200">
          <table className="w-full">
            <thead className="bg-blue-50 border-b border-blue-100 hidden md:table-header-group">
              <tr>
                <th className="p-2 md:p-3 text-center font-semibold text-gray-700 bg-gray-100 w-20">
                  Temps
                </th>
                <th className="p-2 md:p-3 text-center font-semibold text-gray-700 bg-gray-100">
                  Équipe
                </th>
                <th className="p-2 md:p-3 text-center text-xs font-bold text-blue-800 uppercase tracking-wider">Action</th>
                <th className="p-2 md:p-3 text-center text-xs font-bold text-blue-800 uppercase tracking-wider">Joueur</th>
                <th className="p-2 md:p-3 text-center text-xs font-bold text-blue-800 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {eventsToUse.sort((a: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }, b: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }) => (a.minute || 0) - (b.minute || 0)).map((event: MatchEvent | Omit<MatchEvent, "timestamp"> & { timestamp: string }, index: number) => {
                // Définir la couleur et l'icône en fonction du type d'événement
                const eventDetails = {
                  'goal': { 
                    color: 'bg-green-100 text-green-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'goal')?.emoji}</div>
                    ), 
                    label: 'But' 
                  },
                  'jaune': { 
                    color: 'bg-yellow-100 text-yellow-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'jaune')?.emoji}</div>
                    ), 
                    label: 'Carton Jaune' 
                  },
                  'rouge': { 
                    color: 'bg-red-100 text-red-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'rouge')?.emoji}</div>
                    ), 
                    label: 'Carton Rouge' 
                  },
                  'remplacement': { 
                    color: 'bg-blue-100 text-blue-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'remplacement')?.emoji}</div>
                    ), 
                    label: 'Remplacement' 
                  },
                  'occasion': { 
                    color: 'bg-gray-100 text-gray-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'occasion')?.emoji}</div>
                    ), 
                    label: 'Occasion' 
                  },
                  'blesse': { 
                    color: 'bg-gray-100 text-gray-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'blesse')?.emoji}</div>
                    ), 
                    label: 'Blessé' 
                  },
                  'passe_decisive': { 
                    color: 'bg-purple-100 text-purple-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'passe_decisive')?.emoji}</div>
                    ), 
                    label: 'Passe Décisive' 
                  },
                  'tir_cadre': { 
                    color: 'bg-orange-100 text-orange-800', 
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'tir_cadre')?.emoji}</div>
                    ), 
                    label: 'Tirs cadrés' 
                  },
                  'csc': {
                    color: 'bg-red-100 text-red-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'csc')?.emoji}</div>
                    ),
                    label: 'CSC'
                  },
                  'corner': {
                    color: 'bg-indigo-100 text-indigo-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'corner')?.emoji}</div>
                    ),
                    label: 'Corner'
                  },
                  'cfd': {
                    color: 'bg-blue-100 text-blue-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'cfd')?.emoji}</div>
                    ),
                    label: 'Coup franc direct'
                  },
                  'cfi': {
                    color: 'bg-blue-100 text-blue-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'cfi')?.emoji}</div>
                    ),
                    label: 'Coup franc indirect'
                  },
                  'hj': {
                    color: 'bg-pink-100 text-pink-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'hj')?.emoji}</div>
                    ),
                    label: 'Hors-jeu'
                  },
                  'recup': {
                    color: 'bg-green-100 text-green-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'recup')?.emoji}</div>
                    ),
                    label: 'Récupération'
                  },
                  'perte': {
                    color: 'bg-gray-100 text-gray-800',
                    icon: (
                      <div className="text-3xl">{eventTypes.find(e => e.type === 'perte')?.emoji}</div>
                    ),
                    label: 'Perte de balle'
                  }
                };

                const details = eventDetails[event.type as keyof typeof eventDetails] || { 
                  color: 'bg-gray-100 text-gray-800', 
                  icon: (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                  label: event.type
                };
                
                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors flex flex-col md:table-row mb-2 md:mb-0 border-b border-gray-200">
                    <td className="p-2 md:p-3 text-sm font-medium text-center w-full md:w-auto">
                      <div className="md:hidden font-semibold text-gray-500 text-xs mb-1">Temps</div>
                      <div>
                        {event.minute}'{event.seconds ? `:${event.seconds}"` : ''}
                      </div>
                    </td>
                    <td className="p-2 md:p-3 text-sm text-center w-full md:w-auto">
                      <div className="md:hidden font-semibold text-gray-500 text-xs mb-1">Équipe</div>
                      <div>
                        {event.team === 'teamA' ? match.teamA.name : match.teamB.name}
                      </div>
                    </td>
                    <td className={`p-2 md:p-3 text-sm text-center font-semibold w-full md:w-auto ${details.color}`}>
                      <div className="md:hidden font-semibold text-gray-500 text-xs mb-1">Action</div>
                      <div>
                        {details.label}
                      </div>
                    </td>
                    <td className="p-2 md:p-3 text-sm text-center w-full md:w-auto">
                      <div className="md:hidden font-semibold text-gray-500 text-xs mb-1">Joueur</div>
                      <div>
                        {event.player || 'Non spécifié'}
                      </div>
                    </td>
                    <td className="p-2 md:p-3 text-sm text-center w-full md:w-16">
                      <div className="md:hidden font-semibold text-gray-500 text-xs mb-1">Actions</div>
                      <div className="flex justify-center">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette action : ${details.label} à la ${event.minute}' ?`)) {
                              try {
                                const matchId = matchFromRedux.id;
                                if (!matchId) throw new Error('ID du match non trouvé');

                                // Supprimer l'événement de Firebase
                                await deleteMatchEvent(matchId, index);
                                
                                // Mettre à jour l'état local immédiatement pour rafraîchir l'UI
                                setLocalEvents(prevEvents => prevEvents.filter((_, i) => i !== index));
                                
                                // Mettre à jour Redux
                                dispatch(deleteEvent({ eventIndex: index }));

                                // Si c'était un but, mettre à jour le score
                                if (event.type === 'goal') {
                                  const team = event.team as 'teamA' | 'teamB';
                                  const currentScore = team === 'teamA' ? matchFromRedux.scoreA : matchFromRedux.scoreB;
                                  dispatch(updateScore({ 
                                    team, 
                                    score: Math.max(0, currentScore - 1) 
                                  }));
                                }

                                // Recharger le match complet
                                const updatedMatch = await getMatch(matchId);
                                if (updatedMatch) {
                                  // Convertir le match en format sérialisable avant de le dispatcher à Redux
                                  const serializableMatch = convertToSerializableMatch(updatedMatch);
                                  dispatch(setCurrentMatch(serializableMatch));
                                  
                                  // Mettre à jour l'état local avec les événements mis à jour
                                  setLocalEvents(serializableMatch.events || []);
                                }

                              } catch (error) {
                                console.error('Erreur lors de la suppression de l\'événement:', error);
                                alert('Erreur lors de la suppression de l\'événement');
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-800 transition-colors p-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Message si aucun événement */}
          {eventsToUse.length === 0 && (
            <div className="p-6 text-center bg-gray-50">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 font-semibold">Aucun événement enregistré pour ce match</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions en bas de page */}
      <div className="mt-3 md:mt-6 space-y-2 md:space-y-4">
        <h3 className="text-xl font-bold mb-4">Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <button 
              className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
              onClick={() => {
                const shareMenu = document.getElementById('shareMenu');
                if (shareMenu) {
                  shareMenu.classList.toggle('hidden');
                }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span>Partager le Match</span>
            </button>
            
            {/* Menu de partage */}
            <div id="shareMenu" className="hidden absolute z-10 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200">
              <div className="p-2 space-y-2">
                {/* Copier le lien */}
                <button 
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-lg flex items-center space-x-2"
                  onClick={() => {
                    navigator.clipboard.writeText(`Match: ${match.teamA.name} vs ${match.teamB.name}, Score: ${scoreA}-${scoreB}`);
                    alert('Lien du match copié dans le presse-papiers');
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  <span>Copier le lien</span>
                </button>

                {/* Partager par WhatsApp */}
                <button 
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-lg flex items-center space-x-2"
                  onClick={() => {
                    const text = encodeURIComponent(`Match: ${match.teamA.name} vs ${match.teamB.name}\nScore final: ${scoreA}-${scoreB}\nStatistiques complètes: [lien]`);
                    window.open(`https://wa.me/?text=${text}`, '_blank');
                  }}
                >
                  <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Partager via WhatsApp</span>
                </button>

                {/* Exporter en PDF */}
                <button 
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-lg flex items-center space-x-2"
                  onClick={() => {
                    // Créer un nouveau document PDF
                    const doc = new jsPDF() as JsPDFWithAutoTable;
                    
                    // Ajouter le titre
                    doc.setFontSize(20);
                    doc.text(`Match: ${match.teamA.name} vs ${match.teamB.name}`, 20, 20);
                    
                    // Ajouter le score
                    doc.setFontSize(16);
                    doc.text(`Score: ${scoreA} - ${scoreB}`, 20, 30);
                    
                    // Ajouter la date
                    doc.setFontSize(12);
                    doc.text(`Date: ${typeof match.date === 'string' ? new Date(match.date).toLocaleDateString('fr-FR') : new Date((match.date as any).toMillis ? (match.date as any).toMillis() : match.date).toLocaleDateString('fr-FR')}`, 20, 40);
                    
                    // Tableau des statistiques globales
                    doc.autoTable({
                      startY: 50,
                      head: [['Statistiques', 'Total']],
                      body: [
                        ['Buts', totalStats.goals.toString()],
                        ['Passes décisives', totalStats.assists.toString()],
                        ['Cartons Jaunes', totalStats.yellowCards.toString()],
                        ['Cartons Rouges', totalStats.redCards.toString()],
                        ['Remplacements', totalStats.substitutions.toString()],
                        ['Blessés', totalStats.injuries.toString()],
                        ['Occasions', totalStats.chances.toString()],
                        ['Tirs cadrés', totalStats.shotsOnTarget.toString()],
                        ['CSC', totalStats.ownGoals.toString()],
                        ['Corners', totalStats.corners.toString()],
                        ['CFD', totalStats.freeKicksDirect.toString()],
                        ['CFI', totalStats.freeKicksIndirect.toString()],
                        ['Hors-jeu', totalStats.offsides.toString()],
                        ['Récupérations', totalStats.recoveries.toString()],
                        ['Pertes', totalStats.losses.toString()]
                      ],
                    });

                    let finalY = (doc as any).lastAutoTable.finalY;

                    // Statistiques par équipe
                    const teamAY = finalY + 10;
                    doc.text(`Statistiques ${match.teamA.name}`, 20, teamAY);
                    doc.autoTable({
                      startY: teamAY + 5,
                      head: [['Action', 'Nombre', 'Minutes']],
                      body: [
                        ['Buts', teamAStats.goals.count.toString(), teamAStats.goals.times.join(', ')],
                        ['Passes décisives', teamAStats.assists.count.toString(), teamAStats.assists.times.join(', ')],
                        ['Cartons Jaunes', teamAStats.yellowCards.count.toString(), teamAStats.yellowCards.times.join(', ')],
                        ['Cartons Rouges', teamAStats.redCards.count.toString(), teamAStats.redCards.times.join(', ')],
                        ['Remplacements', teamAStats.substitutions.count.toString(), teamAStats.substitutions.times.join(', ')],
                        ['Occasions', teamAStats.chances.count.toString(), teamAStats.chances.times.join(', ')],
                        ['Tirs cadrés', teamAStats.shotsOnTarget.count.toString(), teamAStats.shotsOnTarget.times.join(', ')],
                        ['CSC', teamAStats.ownGoals.count.toString(), teamAStats.ownGoals.times.join(', ')],
                        ['Corners', teamAStats.corners.count.toString(), teamAStats.corners.times.join(', ')],
                        ['CFD', teamAStats.freeKicksDirect.count.toString(), teamAStats.freeKicksDirect.times.join(', ')],
                        ['CFI', teamAStats.freeKicksIndirect.count.toString(), teamAStats.freeKicksIndirect.times.join(', ')],
                        ['Hors-jeu', teamAStats.offsides.count.toString(), teamAStats.offsides.times.join(', ')],
                        ['Récupérations', teamAStats.recoveries.count.toString(), teamAStats.recoveries.times.join(', ')],
                        ['Pertes', teamAStats.losses.count.toString(), teamAStats.losses.times.join(', ')]
                      ],
                    });

                    finalY = (doc as any).lastAutoTable.finalY;

                    // Statistiques équipe B
                    const teamBY = finalY + 10;
                    doc.text(`Statistiques ${match.teamB.name}`, 20, teamBY);
                    doc.autoTable({
                      startY: teamBY + 5,
                      head: [['Action', 'Nombre', 'Minutes']],
                      body: [
                        ['Buts', teamBStats.goals.count.toString(), teamBStats.goals.times.join(', ')],
                        ['Passes décisives', teamBStats.assists.count.toString(), teamBStats.assists.times.join(', ')],
                        ['Cartons Jaunes', teamBStats.yellowCards.count.toString(), teamBStats.yellowCards.times.join(', ')],
                        ['Cartons Rouges', teamBStats.redCards.count.toString(), teamBStats.redCards.times.join(', ')],
                        ['Remplacements', teamBStats.substitutions.count.toString(), teamBStats.substitutions.times.join(', ')],
                        ['Occasions', teamBStats.chances.count.toString(), teamBStats.chances.times.join(', ')],
                        ['Tirs cadrés', teamBStats.shotsOnTarget.count.toString(), teamBStats.shotsOnTarget.times.join(', ')],
                        ['CSC', teamBStats.ownGoals.count.toString(), teamBStats.ownGoals.times.join(', ')],
                        ['Corners', teamBStats.corners.count.toString(), teamBStats.corners.times.join(', ')],
                        ['CFD', teamBStats.freeKicksDirect.count.toString(), teamBStats.freeKicksDirect.times.join(', ')],
                        ['CFI', teamBStats.freeKicksIndirect.count.toString(), teamBStats.freeKicksIndirect.times.join(', ')],
                        ['Hors-jeu', teamBStats.offsides.count.toString(), teamBStats.offsides.times.join(', ')],
                        ['Récupérations', teamBStats.recoveries.count.toString(), teamBStats.recoveries.times.join(', ')],
                        ['Pertes', teamBStats.losses.count.toString(), teamBStats.losses.times.join(', ')]
                      ],
                    });

                    finalY = (doc as any).lastAutoTable.finalY;

                    // Statistiques par période
                    if (calculatedPeriodStats.length > 0) {
                      doc.addPage();
                      doc.text('Statistiques par période', 20, 20);
                      
                      calculatedPeriodStats.forEach((period: any, index: number) => {
                        const startY = index === 0 ? 30 : (doc as any).lastAutoTable.finalY + 10;
                        doc.text(period.periodName, 20, startY);
                        doc.autoTable({
                          startY: startY + 5,
                          head: [['Statistique', 'Nombre']],
                          body: [
                            ['Buts', period.stats.goals.toString()],
                            ['Passes décisives', period.stats.assists.toString()],
                            ['Cartons Jaunes', period.stats.yellowCards.toString()],
                            ['Cartons Rouges', period.stats.redCards.toString()],
                            ['Remplacements', period.stats.substitutions.toString()],
                            ['Occasions', period.stats.chances.toString()],
                            ['Tirs cadrés', period.stats.shotsOnTarget.toString()],
                            ['CSC', period.stats.ownGoals.toString()],
                            ['Corners', period.stats.corners.toString()],
                            ['CFD', period.stats.freeKicksDirect.toString()],
                            ['CFI', period.stats.freeKicksIndirect.toString()],
                            ['Hors-jeu', period.stats.offsides.toString()],
                            ['Récupérations', period.stats.recoveries.toString()],
                            ['Pertes', period.stats.losses.toString()]
                          ],
                        });
                      });
                    }

                    // Sauvegarder le PDF
                    doc.save(`match_${match.teamA.name}_vs_${match.teamB.name}.pdf`);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7.862a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span>Exporter en PDF</span>
                </button>

                {/* Bouton Excel/Google Sheet */}
                <button 
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded-lg flex items-center space-x-2"
                  onClick={handleExportExcel}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Exporter en Excel/Google Sheet</span>
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button 
              className="w-full bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
              onClick={() => {
                // Créer un nouveau document PDF
                const doc = new jsPDF() as JsPDFWithAutoTable;
                
                // Ajouter le titre
                doc.setFontSize(20);
                doc.text(`Match: ${match.teamA.name} vs ${match.teamB.name}`, 20, 20);
                
                // Ajouter le score
                doc.setFontSize(16);
                doc.text(`Score: ${scoreA} - ${scoreB}`, 20, 30);
                
                // Ajouter la date
                doc.setFontSize(12);
                doc.text(`Date: ${typeof match.date === 'string' ? new Date(match.date).toLocaleDateString('fr-FR') : new Date((match.date as any).toMillis ? (match.date as any).toMillis() : match.date).toLocaleDateString('fr-FR')}`, 20, 40);
                
                // Tableau des statistiques globales
                doc.autoTable({
                  startY: 50,
                  head: [['Statistiques', 'Total']],
                  body: [
                    ['Buts', totalStats.goals.toString()],
                    ['Passes décisives', totalStats.assists.toString()],
                    ['Cartons Jaunes', totalStats.yellowCards.toString()],
                    ['Cartons Rouges', totalStats.redCards.toString()],
                    ['Remplacements', totalStats.substitutions.toString()],
                    ['Blessés', totalStats.injuries.toString()],
                    ['Occasions', totalStats.chances.toString()],
                    ['Tirs cadrés', totalStats.shotsOnTarget.toString()],
                    ['CSC', totalStats.ownGoals.toString()],
                    ['Corners', totalStats.corners.toString()],
                    ['CFD', totalStats.freeKicksDirect.toString()],
                    ['CFI', totalStats.freeKicksIndirect.toString()],
                    ['Hors-jeu', totalStats.offsides.toString()],
                    ['Récupérations', totalStats.recoveries.toString()],
                    ['Pertes', totalStats.losses.toString()]
                  ],
                });
                
                let finalY = (doc as any).lastAutoTable.finalY;
                
                // Statistiques par équipe
                const teamAY = finalY + 10;
                doc.text(`Statistiques ${match.teamA.name}`, 20, teamAY);
                doc.autoTable({
                  startY: teamAY + 5,
                  head: [['Action', 'Nombre', 'Minutes']],
                  body: [
                    ['Buts', teamAStats.goals.count.toString(), teamAStats.goals.times.join(', ')],
                    ['Passes décisives', teamAStats.assists.count.toString(), teamAStats.assists.times.join(', ')],
                    ['Cartons Jaunes', teamAStats.yellowCards.count.toString(), teamAStats.yellowCards.times.join(', ')],
                    ['Cartons Rouges', teamAStats.redCards.count.toString(), teamAStats.redCards.times.join(', ')],
                    ['Remplacements', teamAStats.substitutions.count.toString(), teamAStats.substitutions.times.join(', ')],
                    ['Occasions', teamAStats.chances.count.toString(), teamAStats.chances.times.join(', ')],
                    ['Tirs cadrés', teamAStats.shotsOnTarget.count.toString(), teamAStats.shotsOnTarget.times.join(', ')],
                    ['CSC', teamAStats.ownGoals.count.toString(), teamAStats.ownGoals.times.join(', ')],
                    ['Corners', teamAStats.corners.count.toString(), teamAStats.corners.times.join(', ')],
                    ['CFD', teamAStats.freeKicksDirect.count.toString(), teamAStats.freeKicksDirect.times.join(', ')],
                    ['CFI', teamAStats.freeKicksIndirect.count.toString(), teamAStats.freeKicksIndirect.times.join(', ')],
                    ['Hors-jeu', teamAStats.offsides.count.toString(), teamAStats.offsides.times.join(', ')],
                    ['Récupérations', teamAStats.recoveries.count.toString(), teamAStats.recoveries.times.join(', ')],
                    ['Pertes', teamAStats.losses.count.toString(), teamAStats.losses.times.join(', ')]
                  ],
                });
                
                finalY = (doc as any).lastAutoTable.finalY;
                
                const teamBY = finalY + 10;
                doc.text(`Statistiques ${match.teamB.name}`, 20, teamBY);
                doc.autoTable({
                  startY: teamBY + 5,
                  head: [['Action', 'Nombre', 'Minutes']],
                  body: [
                    ['Buts', teamBStats.goals.count.toString(), teamBStats.goals.times.join(', ')],
                    ['Passes décisives', teamBStats.assists.count.toString(), teamBStats.assists.times.join(', ')],
                    ['Cartons Jaunes', teamBStats.yellowCards.count.toString(), teamBStats.yellowCards.times.join(', ')],
                    ['Cartons Rouges', teamBStats.redCards.count.toString(), teamBStats.redCards.times.join(', ')],
                    ['Remplacements', teamBStats.substitutions.count.toString(), teamBStats.substitutions.times.join(', ')],
                    ['Occasions', teamBStats.chances.count.toString(), teamBStats.chances.times.join(', ')],
                    ['Tirs cadrés', teamBStats.shotsOnTarget.count.toString(), teamBStats.shotsOnTarget.times.join(', ')],
                    ['CSC', teamBStats.ownGoals.count.toString(), teamBStats.ownGoals.times.join(', ')],
                    ['Corners', teamBStats.corners.count.toString(), teamBStats.corners.times.join(', ')],
                    ['CFD', teamBStats.freeKicksDirect.count.toString(), teamBStats.freeKicksDirect.times.join(', ')],
                    ['CFI', teamBStats.freeKicksIndirect.count.toString(), teamBStats.freeKicksIndirect.times.join(', ')],
                    ['Hors-jeu', teamBStats.offsides.count.toString(), teamBStats.offsides.times.join(', ')],
                    ['Récupérations', teamBStats.recoveries.count.toString(), teamBStats.recoveries.times.join(', ')],
                    ['Pertes', teamBStats.losses.count.toString(), teamBStats.losses.times.join(', ')]
                  ],
                });
                
                // Enregistrer le PDF
                doc.save(`match_${match.teamA.name}_vs_${match.teamB.name}.pdf`);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7.862a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span>Exporter en PDF</span>
            </button>
            
            <button 
              className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
              onClick={handleExportExcel}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Exporter en Excel</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchStats;
